import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { isSupabaseConfigured, supabase } from '../supabase';
import type { CalendarEvent } from '../types';
import { TOWNS } from '../constants/towns';
import './Scheduler.css';

type EventRow = CalendarEvent & { category?: { name: string } | null };
type DemandLevel = 'low' | 'medium' | 'high';
type Daypart = 'morning' | 'afternoon' | 'evening' | 'late-night';
type PeriodPreset = 'next90' | 'next180' | 'yearAhead' | 'financialYear' | 'custom';
type BusinessType = 'hospitality' | 'retail' | 'accommodation' | 'tourism' | 'services';

type DailyDemandPoint = {
  iso: string;
  label: string;
  count: number;
  weekday: number;
  monthKey: string;
};

type MonthDemandPoint = {
  key: string;
  label: string;
  count: number;
  level: DemandLevel;
};

type AvailabilityBlackout = { id: string; from: string; to: string };

type YearAheadAnalysis = {
  totalEvents: number;
  averageDailyDemand: number;
  averageDemandOnOperatingDays: number;
  calendarDays: number;
  operatingDays: number;
  closedDays: number;
  peakDemand: number;
  bestDay: DailyDemandPoint | null;
  monthly: MonthDemandPoint[];
  weekdayDemand: { label: string; count: number }[];
  daypartDemand: { key: Daypart; label: string; percentage: number }[];
  topDemandDays: DailyDemandPoint[];
  totalDemandUnits: number;
  categoryDemand: { label: string; count: number; share: number }[];
  busiestStreak: { start: string; end: string; days: number; avgDemand: number } | null;
  quietestMonth: MonthDemandPoint | null;
};

type TempJobRecommendation = {
  role: string;
  shiftsPerWeek: number;
  shift: string;
  window: string;
  reason: string;
};

const YAA_REPORT_STEPS = [
  'Headline snapshot',
  'Seasonality',
  'Category mix',
  'Day + time of day',
  'Peak pressure',
  'Temp staffing plan',
  'Hiring timeline',
  'Budget scenarios',
  'PDF report',
  'Checklist',
] as const;

const DAYPART_LABELS: Record<Daypart, string> = {
  morning: 'Morning surge',
  afternoon: 'Afternoon surge',
  evening: 'Evening surge',
  'late-night': 'Late-night surge',
};

const DAYPART_SHIFT_WINDOWS: Record<Daypart, string> = {
  morning: '06:30 - 12:30',
  afternoon: '11:30 - 17:30',
  evening: '16:30 - 22:30',
  'late-night': '21:00 - 01:30',
};

const BUSINESS_ROLE_TEMPLATES: Record<
  BusinessType,
  { role: string; baseRatio: number; preferredDayparts: Daypart[]; reason: string }[]
> = {
  hospitality: [
    {
      role: 'Front of House Casual',
      baseRatio: 0.34,
      preferredDayparts: ['afternoon', 'evening'],
      reason: 'Handles event-driven walk-ins and rapid table turnover.',
    },
    {
      role: 'Bar / Beverage Staff',
      baseRatio: 0.22,
      preferredDayparts: ['evening', 'late-night'],
      reason: 'Covers peak drink service during headline periods.',
    },
    {
      role: 'Prep / Line Support',
      baseRatio: 0.18,
      preferredDayparts: ['morning', 'afternoon'],
      reason: 'Improves ticket speed when kitchen volume spikes.',
    },
  ],
  retail: [
    {
      role: 'Floor Sales Assistant',
      baseRatio: 0.3,
      preferredDayparts: ['afternoon', 'morning'],
      reason: 'Supports customer flow during event shopping rushes.',
    },
    {
      role: 'POS + Click-and-Collect',
      baseRatio: 0.24,
      preferredDayparts: ['afternoon', 'evening'],
      reason: 'Prevents checkout bottlenecks at local peak windows.',
    },
    {
      role: 'Stock Replenishment',
      baseRatio: 0.16,
      preferredDayparts: ['morning', 'late-night'],
      reason: 'Keeps high-turn inventory available for peak days.',
    },
  ],
  accommodation: [
    {
      role: 'Housekeeping Casual',
      baseRatio: 0.32,
      preferredDayparts: ['morning', 'afternoon'],
      reason: 'Meets compressed turnaround around event dates.',
    },
    {
      role: 'Guest Services / Reception',
      baseRatio: 0.22,
      preferredDayparts: ['afternoon', 'evening'],
      reason: 'Manages clustered check-ins and late arrivals.',
    },
    {
      role: 'Night Duty Support',
      baseRatio: 0.16,
      preferredDayparts: ['late-night', 'evening'],
      reason: 'Covers after-hours demand and guest requests.',
    },
  ],
  tourism: [
    {
      role: 'Guide / Tour Host',
      baseRatio: 0.3,
      preferredDayparts: ['morning', 'afternoon'],
      reason: 'Expands session capacity when inbound demand rises.',
    },
    {
      role: 'Ticketing + Guest Assist',
      baseRatio: 0.21,
      preferredDayparts: ['afternoon', 'evening'],
      reason: 'Reduces queue times at attraction entry points.',
    },
    {
      role: 'Transport / Marshal Casual',
      baseRatio: 0.17,
      preferredDayparts: ['afternoon', 'evening'],
      reason: 'Improves guest movement between venues and activities.',
    },
  ],
  services: [
    {
      role: 'Customer Support Casual',
      baseRatio: 0.24,
      preferredDayparts: ['morning', 'afternoon'],
      reason: 'Maintains response times during local demand spikes.',
    },
    {
      role: 'On-site Operations Support',
      baseRatio: 0.19,
      preferredDayparts: ['afternoon', 'evening'],
      reason: 'Adds execution capacity around high-volume periods.',
    },
    {
      role: 'Admin / Back Office Relief',
      baseRatio: 0.14,
      preferredDayparts: ['morning'],
      reason: 'Protects core team from planning and admin overload.',
    },
  ],
};

function toIsoDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function eachDay(from: string, to: string) {
  const out: Date[] = [];
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) out.push(new Date(d));
  return out;
}

function overlapOnDate(event: EventRow, dateIso: string) {
  return event.start_date <= dateIso && event.end_date >= dateIso;
}

function isOperatingDay(
  iso: string,
  weekday: number,
  operatingWeekdays: boolean[],
  blackouts: { from: string; to: string }[],
): boolean {
  if (!operatingWeekdays[weekday]) return false;
  for (const b of blackouts) {
    if (b.from && b.to && iso >= b.from && iso <= b.to) return false;
  }
  return true;
}

function countOperatingDaysInRange(
  fromIso: string,
  toIso: string,
  operatingWeekdays: boolean[],
  blackouts: { from: string; to: string }[],
): number {
  return eachDay(fromIso, toIso).filter((d) =>
    isOperatingDay(toIsoDate(d), d.getDay(), operatingWeekdays, blackouts),
  ).length;
}

function formatDateRange(startIso: string, endIso: string) {
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
  if (sameMonth) {
    return `${start.toLocaleDateString('en-AU', { day: 'numeric' })}-${end.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })}`;
  }
  return `${start.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString(
    'en-AU',
    { day: 'numeric', month: 'short', year: 'numeric' },
  )}`;
}

function monthKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key: string) {
  const [yearRaw, monthRaw] = key.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
}

function demandLevel(value: number, lowBreak: number, highBreak: number): DemandLevel {
  if (value >= highBreak) return 'high';
  if (value >= lowBreak) return 'medium';
  return 'low';
}

function overlapDaysInRange(event: EventRow, fromIso: string, toIso: string): number {
  const start = new Date(`${event.start_date > fromIso ? event.start_date : fromIso}T00:00:00`);
  const end = new Date(`${event.end_date < toIso ? event.end_date : toIso}T00:00:00`);
  if (end < start) return 0;
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.floor((end.getTime() - start.getTime()) / msPerDay) + 1;
}

function inferEventDaypartWeights(event: EventRow): Record<Daypart, number> {
  const title = event.title.toLowerCase();
  const category = event.category?.name?.toLowerCase() ?? '';

  const base: Record<Daypart, number> = {
    morning: 0.9,
    afternoon: 1,
    evening: 1.2,
    'late-night': 0.5,
  };

  if (/music|concert|festival|night|gig/.test(title) || /music/.test(category)) {
    base.evening += 1.4;
    base['late-night'] += 0.9;
  }
  if (/market|expo|show|fair|family|community/.test(title) || /cultural|industry/.test(category)) {
    base.morning += 0.7;
    base.afternoon += 1.1;
  }
  if (/sport|match|cup|race|tournament/.test(title) || /sporting/.test(category)) {
    base.afternoon += 1.25;
    base.evening += 0.7;
  }
  if (/dinner|cocktail|awards/.test(title)) {
    base.evening += 1.2;
    base['late-night'] += 0.5;
  }

  return base;
}

function buildPeriodBounds(preset: PeriodPreset): { from: string; to: string } {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end = new Date(start);
  if (preset === 'next90') end.setDate(end.getDate() + 90);
  if (preset === 'next180') end.setDate(end.getDate() + 180);
  if (preset === 'yearAhead') end.setFullYear(end.getFullYear() + 1);
  if (preset === 'financialYear') {
    const fyStartYear = start.getMonth() >= 6 ? start.getFullYear() : start.getFullYear() - 1;
    const fyStart = new Date(fyStartYear, 6, 1);
    const fyEnd = new Date(fyStartYear + 1, 5, 30);
    return { from: toIsoDate(fyStart), to: toIsoDate(fyEnd) };
  }
  return { from: toIsoDate(start), to: toIsoDate(end) };
}

function buildAnalysis(
  events: EventRow[],
  fromIso: string,
  toIso: string,
  operatingWeekdays: boolean[],
  blackouts: { from: string; to: string }[],
): YearAheadAnalysis {
  const days = eachDay(fromIso, toIso);
  const monthCounter = new Map<string, number>();
  const weekdayCounter = new Map<number, number>();
  const categoryCounter = new Map<string, number>();

  days.forEach((d) => {
    monthCounter.set(monthKey(d), 0);
    weekdayCounter.set(d.getDay(), 0);
  });

  const daily: DailyDemandPoint[] = days.map((day) => {
    const iso = toIsoDate(day);
    const count = events.filter((event) => overlapOnDate(event, iso)).length;
    const key = monthKey(day);
    monthCounter.set(key, (monthCounter.get(key) ?? 0) + count);
    weekdayCounter.set(day.getDay(), (weekdayCounter.get(day.getDay()) ?? 0) + count);
    return {
      iso,
      label: day.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }),
      count,
      weekday: day.getDay(),
      monthKey: key,
    };
  });

  const maxDayDemand = daily.reduce((acc, point) => Math.max(acc, point.count), 0);
  const calendarDays = daily.length;
  const averageDailyDemand = calendarDays
    ? Number((daily.reduce((sum, point) => sum + point.count, 0) / calendarDays).toFixed(2))
    : 0;
  const operatingDaily = daily.filter((point) =>
    isOperatingDay(point.iso, point.weekday, operatingWeekdays, blackouts),
  );
  const operatingDays = operatingDaily.length;
  const closedDays = calendarDays - operatingDays;
  const averageDemandOnOperatingDays = operatingDays
    ? Number((operatingDaily.reduce((sum, point) => sum + point.count, 0) / operatingDays).toFixed(2))
    : 0;

  const monthValues = [...monthCounter.values()];
  const monthHigh = monthValues.length ? Math.max(...monthValues) : 0;
  const monthMid = monthHigh * 0.5;

  const monthly: MonthDemandPoint[] = [...monthCounter.entries()].map(([key, count]) => ({
    key,
    label: monthLabel(key),
    count,
    level: demandLevel(count, monthMid, monthHigh * 0.82),
  }));

  const weekdayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weekdayDemand = weekdayNames.map((label, dayIndex) => ({
    label,
    count: weekdayCounter.get(dayIndex) ?? 0,
  }));

  const daypartTotals: Record<Daypart, number> = {
    morning: 0,
    afternoon: 0,
    evening: 0,
    'late-night': 0,
  };

  for (const event of events) {
    const overlapDays = overlapDaysInRange(event, fromIso, toIso);
    if (!overlapDays) continue;
    const weights = inferEventDaypartWeights(event);
    daypartTotals.morning += weights.morning * overlapDays;
    daypartTotals.afternoon += weights.afternoon * overlapDays;
    daypartTotals.evening += weights.evening * overlapDays;
    daypartTotals['late-night'] += weights['late-night'] * overlapDays;
    const categoryLabel = event.category?.name ?? 'Uncategorised';
    categoryCounter.set(categoryLabel, (categoryCounter.get(categoryLabel) ?? 0) + overlapDays);
  }

  const daypartSum = Object.values(daypartTotals).reduce((sum, value) => sum + value, 0);
  const daypartDemand: { key: Daypart; label: string; percentage: number }[] = (
    Object.keys(daypartTotals) as Daypart[]
  ).map((key) => ({
    key,
    label: DAYPART_LABELS[key],
    percentage: daypartSum ? Math.round((daypartTotals[key] / daypartSum) * 100) : 0,
  }));
  daypartDemand.sort((a, b) => b.percentage - a.percentage);

  const topDemandDays = [...daily]
    .sort((a, b) => {
      if (b.count === a.count) return a.iso.localeCompare(b.iso);
      return b.count - a.count;
    })
    .slice(0, 8);
  const totalDemandUnits = monthly.reduce((sum, item) => sum + item.count, 0);
  const categoryDemand = [...categoryCounter.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([label, count]) => ({
      label,
      count,
      share: totalDemandUnits ? Math.round((count / totalDemandUnits) * 100) : 0,
    }));

  const streakThreshold = Math.max(
    1,
    Math.round(Math.max(averageDemandOnOperatingDays || averageDailyDemand, maxDayDemand * 0.58)),
  );
  let bestStreak: YearAheadAnalysis['busiestStreak'] = null;
  let activeStart: string | null = null;
  let activeDays = 0;
  let activeDemandTotal = 0;

  for (const point of daily) {
    if (point.count >= streakThreshold) {
      if (!activeStart) activeStart = point.iso;
      activeDays += 1;
      activeDemandTotal += point.count;
    } else if (activeStart) {
      const endIso = shiftIsoDays(point.iso, -1);
      if (!bestStreak || activeDays > bestStreak.days) {
        bestStreak = {
          start: activeStart,
          end: endIso,
          days: activeDays,
          avgDemand: Number((activeDemandTotal / activeDays).toFixed(1)),
        };
      }
      activeStart = null;
      activeDays = 0;
      activeDemandTotal = 0;
    }
  }
  if (activeStart && activeDays) {
    const endIso = daily[daily.length - 1]?.iso ?? activeStart;
    if (!bestStreak || activeDays > bestStreak.days) {
      bestStreak = {
        start: activeStart,
        end: endIso,
        days: activeDays,
        avgDemand: Number((activeDemandTotal / activeDays).toFixed(1)),
      };
    }
  }

  const quietestMonth = [...monthly].sort((a, b) => a.count - b.count)[0] ?? null;

  return {
    totalEvents: events.length,
    averageDailyDemand,
    averageDemandOnOperatingDays,
    calendarDays,
    operatingDays,
    closedDays,
    peakDemand: maxDayDemand,
    bestDay: topDemandDays[0] ?? null,
    monthly,
    weekdayDemand,
    daypartDemand,
    topDemandDays,
    totalDemandUnits,
    categoryDemand,
    busiestStreak: bestStreak,
    quietestMonth,
  };
}

function buildTempPlan(
  analysis: YearAheadAnalysis,
  businessType: BusinessType,
  baseTeamSize: number,
): TempJobRecommendation[] {
  const topMonths = [...analysis.monthly]
    .filter((m) => m.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);
  const windowLabel = topMonths.length ? topMonths.map((m) => m.label).join(' / ') : 'Low-demand period';

  const topDayparts = analysis.daypartDemand.slice(0, 2).map((d) => d.key);
  const demandForPressure = analysis.operatingDays > 0 ? analysis.averageDemandOnOperatingDays : analysis.averageDailyDemand;
  const pressureScore = Math.min(1.25, demandForPressure / 5 + analysis.peakDemand / 18);

  return BUSINESS_ROLE_TEMPLATES[businessType].map((template) => {
    const hasDaypartMatch = template.preferredDayparts.some((dp) => topDayparts.includes(dp));
    const daypartBoost = hasDaypartMatch ? 1.22 : 1;
    const shiftsPerWeek = Math.max(1, Math.round(baseTeamSize * template.baseRatio * (1 + pressureScore) * daypartBoost));
    const primaryDaypart = template.preferredDayparts[0] ?? 'afternoon';
    const shift = DAYPART_SHIFT_WINDOWS[primaryDaypart];
    return {
      role: template.role,
      shiftsPerWeek,
      shift,
      window: windowLabel,
      reason: template.reason,
    };
  });
}

function monthPathPoints(monthly: MonthDemandPoint[], width: number, height: number, padding: number) {
  if (!monthly.length) return '';
  const maxValue = Math.max(...monthly.map((m) => m.count), 1);
  const usableW = width - padding * 2;
  const usableH = height - padding * 2;
  return monthly
    .map((month, index) => {
      const x = padding + (monthly.length === 1 ? usableW / 2 : (index / (monthly.length - 1)) * usableW);
      const y = padding + usableH - (month.count / maxValue) * usableH;
      return `${x},${y}`;
    })
    .join(' ');
}

function conicDaypartGradient(dayparts: YearAheadAnalysis['daypartDemand']) {
  if (!dayparts.length) return '#e2e8f0';
  const colorMap: Record<Daypart, string> = {
    morning: '#3b82f6',
    afternoon: '#06b6d4',
    evening: '#8b5cf6',
    'late-night': '#f43f5e',
  };
  let cursor = 0;
  const segments = dayparts.map((part) => {
    const start = cursor;
    const end = cursor + part.percentage;
    cursor = end;
    return `${colorMap[part.key]} ${start}% ${end}%`;
  });
  return `conic-gradient(${segments.join(', ')})`;
}

function shiftIsoDays(iso: string, deltaDays: number) {
  const date = new Date(`${iso}T00:00:00`);
  date.setDate(date.getDate() + deltaDays);
  return toIsoDate(date);
}

function monthStartIso(key: string) {
  const [yearRaw, monthRaw] = key.split('-');
  return `${yearRaw}-${monthRaw}-01`;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const LJP_NENW_TITLE = 'Local Jobs Program — New England North West (NENW)';

const PDF_DAYPART_HEX: Record<Daypart, string> = {
  morning: '#2563eb',
  afternoon: '#0891b2',
  evening: '#7c3aed',
  'late-night': '#e11d48',
};

function buildPdfMonthlyTrendSvg(monthly: MonthDemandPoint[], w: number, h: number): string {
  if (!monthly.length) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="80" viewBox="0 0 ${w} 80"><rect width="100%" height="100%" fill="#f1f5f9"/><text x="24" y="44" fill="#64748b" font-size="12" font-family="system-ui,sans-serif">No monthly data</text></svg>`;
  }
  const max = Math.max(...monthly.map((m) => m.count), 1);
  const pad = 36;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2 - 22;
  const points = monthly
    .map((m, i) => {
      const x = monthly.length <= 1 ? pad + innerW / 2 : pad + (i / Math.max(monthly.length - 1, 1)) * innerW;
      const y = pad + innerH - (m.count / max) * innerH;
      return `${x},${y}`;
    })
    .join(' ');
  const circles = monthly
    .map((m, i) => {
      const x = monthly.length <= 1 ? pad + innerW / 2 : pad + (i / Math.max(monthly.length - 1, 1)) * innerW;
      const y = pad + innerH - (m.count / max) * innerH;
      return `<circle cx="${x}" cy="${y}" r="5" fill="#16a34a" stroke="#fff" stroke-width="2"/>`;
    })
    .join('');
  const labels = monthly
    .map((m, i) => {
      const x = monthly.length <= 1 ? pad + innerW / 2 : pad + (i / Math.max(monthly.length - 1, 1)) * innerW;
      return `<text x="${x}" y="${h - 10}" text-anchor="middle" fill="#475569" font-size="10" font-family="system-ui,sans-serif">${escapeHtml(m.label)}</text>`;
    })
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><rect width="100%" height="100%" fill="#f1f5f9"/><text x="${pad}" y="22" fill="#0f172a" font-size="13" font-weight="700" font-family="system-ui,sans-serif">Monthly demand trend</text><polyline fill="none" stroke="#15803d" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" points="${points}"/>${circles}${labels}</svg>`;
}

function buildPdfMonthlyBarsSvg(monthly: MonthDemandPoint[], maxCount: number): string {
  if (!monthly.length) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="60" viewBox="0 0 640 60"><rect width="100%" height="100%" fill="#fff"/><text x="8" y="36" fill="#64748b" font-size="12" font-family="system-ui,sans-serif">No monthly data</text></svg>`;
  }
  const w = 640;
  const rowH = 22;
  const h = 48 + monthly.length * rowH;
  const max = Math.max(maxCount, 1);
  const bars = monthly
    .map((m, i) => {
      const y = 40 + i * rowH;
      const bw = Math.round((m.count / max) * 420);
      return `<text x="8" y="${y + 14}" fill="#334155" font-size="11" font-family="system-ui,sans-serif">${escapeHtml(m.label)}</text><rect x="100" y="${y + 2}" width="${Math.max(bw, 4)}" height="16" rx="4" fill="#22c55e"/><text x="${108 + Math.max(bw, 4)}" y="${y + 14}" fill="#64748b" font-size="10" font-family="system-ui,sans-serif">${m.count}</text>`;
    })
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><rect width="100%" height="100%" fill="#fff"/><text x="8" y="24" fill="#0f172a" font-size="13" font-weight="700" font-family="system-ui,sans-serif">Monthly demand (bars)</text>${bars}</svg>`;
}

function buildPdfWeekdaySvg(weekdayDemand: { label: string; count: number }[]): string {
  if (!weekdayDemand.length) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="60" viewBox="0 0 640 60"><rect width="100%" height="100%" fill="#fff"/><text x="8" y="36" fill="#64748b" font-size="12" font-family="system-ui,sans-serif">No weekday data</text></svg>`;
  }
  const max = Math.max(...weekdayDemand.map((d) => d.count), 1);
  const w = 640;
  const rowH = 26;
  const h = 44 + weekdayDemand.length * rowH;
  const rows = weekdayDemand
    .map((d, i) => {
      const y = 36 + i * rowH;
      const bw = Math.round((d.count / max) * 400);
      return `<text x="8" y="${y + 16}" fill="#334155" font-size="12" font-weight="600" font-family="system-ui,sans-serif">${escapeHtml(d.label)}</text><rect x="52" y="${y + 4}" width="${Math.max(bw, 3)}" height="16" rx="4" fill="#0ea5e9"/><text x="${60 + Math.max(bw, 3)}" y="${y + 16}" fill="#64748b" font-size="10" font-family="system-ui,sans-serif">${d.count}</text>`;
    })
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><rect width="100%" height="100%" fill="#fff"/><text x="8" y="22" fill="#0f172a" font-size="13" font-weight="700" font-family="system-ui,sans-serif">Demand by weekday</text>${rows}</svg>`;
}

function buildPdfDaypartSvg(dayparts: { key: Daypart; label: string; percentage: number }[]): string {
  const w = 640;
  const h = 200;
  if (!dayparts.length) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="80" viewBox="0 0 ${w} 80"><rect width="100%" height="100%" fill="#fff"/><text x="40" y="44" fill="#64748b" font-size="12" font-family="system-ui,sans-serif">No daypart data</text></svg>`;
  }
  let cursor = 40;
  const barMax = 520;
  const segments: string[] = [];
  for (const p of dayparts) {
    const bw = Math.round((p.percentage / 100) * barMax);
    const seg = Math.max(bw, 2);
    const mid = cursor + seg / 2;
    segments.push(
      `<rect x="${cursor}" y="56" width="${seg}" height="32" rx="4" fill="${PDF_DAYPART_HEX[p.key]}"/><text x="${mid}" y="76" text-anchor="middle" fill="#ffffff" font-size="11" font-weight="700" font-family="system-ui,sans-serif">${p.percentage}%</text>`,
    );
    cursor += seg + 2;
  }
  const legend = dayparts
    .map((p, i) => {
      const ly = 108 + i * 18;
      return `<rect x="40" y="${ly}" width="12" height="12" rx="2" fill="${PDF_DAYPART_HEX[p.key]}"/><text x="58" y="${ly + 11}" fill="#334155" font-size="11" font-family="system-ui,sans-serif">${escapeHtml(p.label)}</text>`;
    })
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><rect width="100%" height="100%" fill="#fff"/><text x="40" y="28" fill="#0f172a" font-size="13" font-weight="700" font-family="system-ui,sans-serif">Daypart mix (estimated)</text><text x="40" y="46" fill="#64748b" font-size="10" font-family="system-ui,sans-serif">Share of weighted event-day demand</text>${segments.join('')}${legend}</svg>`;
}

function buildPdfCategoryBarsSvg(categories: { label: string; count: number; share: number }[]): string {
  const max = Math.max(...categories.map((c) => c.count), 1);
  const w = 640;
  const rowH = 24;
  const h = 40 + categories.length * rowH;
  const rows = categories
    .map((c, i) => {
      const y = 32 + i * rowH;
      const bw = Math.round((c.count / max) * 380);
      return `<text x="8" y="${y + 15}" fill="#334155" font-size="11" font-family="system-ui,sans-serif">${escapeHtml(c.label)}</text><rect x="120" y="${y + 3}" width="${Math.max(bw, 3)}" height="15" rx="3" fill="#8b5cf6"/><text x="${128 + Math.max(bw, 3)}" y="${y + 15}" fill="#64748b" font-size="10" font-family="system-ui,sans-serif">${c.share}%</text>`;
    })
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><rect width="100%" height="100%" fill="#fff"/><text x="8" y="20" fill="#0f172a" font-size="13" font-weight="700" font-family="system-ui,sans-serif">Category mix</text>${rows}</svg>`;
}

export default function Scheduler() {
  const [town, setTown] = useState<string>('regional');
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('yearAhead');
  const initialBounds = useMemo(() => buildPeriodBounds('yearAhead'), []);
  const [from, setFrom] = useState<string>(initialBounds.from);
  const [to, setTo] = useState<string>(initialBounds.to);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<YearAheadAnalysis | null>(null);
  const [businessType, setBusinessType] = useState<BusinessType>('hospitality');
  const [baseTeamSize, setBaseTeamSize] = useState(8);
  const [hourlyRate, setHourlyRate] = useState(36);
  const [shiftHours, setShiftHours] = useState(6);
  const [leadWeeks, setLeadWeeks] = useState(5);
  const [trainingDays, setTrainingDays] = useState(2);
  const [agencyBufferPercent, setAgencyBufferPercent] = useState(12);
  const [annualBudgetCap, setAnnualBudgetCap] = useState<number | ''>('');
  const [tempPlan, setTempPlan] = useState<TempJobRecommendation[]>([]);
  const [phase, setPhase] = useState<'setup' | 'insights'>('setup');
  const [operatingWeekdays, setOperatingWeekdays] = useState<boolean[]>(() => Array(7).fill(true));
  const [blackouts, setBlackouts] = useState<AvailabilityBlackout[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [checklist, setChecklist] = useState<Record<string, boolean>>({
    lockRoster: false,
    postJobs: false,
    confirmTraining: false,
    checkSuppliers: false,
  });

  async function runYearAhead() {
    if (!isSupabaseConfigured) {
      setError('Database connection is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your environment.');
      return;
    }
    if (!from || !to || from > to) {
      setError('Please enter a valid date range.');
      return;
    }
    setLoading(true);
    setError(null);
    let query = supabase
      .from('events')
      .select('*, category:event_categories(name)')
      .eq('status', 'published')
      .gte('end_date', from)
      .lte('start_date', to)
      .order('start_date', { ascending: true });
    const selectedTown = TOWNS.find((t) => t.slug === town);
    if (selectedTown && selectedTown.slug !== 'regional') query = query.ilike('location', `%${selectedTown.name}%`);
    const { data, error: e } = await query;
    setLoading(false);
    if (e) {
      setError(e.message);
      return;
    }
    const fetchedEvents = (data as EventRow[]) ?? [];
    const built = buildAnalysis(fetchedEvents, from, to, operatingWeekdays, blackouts);
    setAnalysis(built);
    setTempPlan(buildTempPlan(built, businessType, baseTeamSize));
    setStepIndex(0);
    setPhase('insights');
  }

  const strongestWeekday = useMemo(() => {
    if (!analysis?.weekdayDemand.length) return null;
    return [...analysis.weekdayDemand].sort((a, b) => b.count - a.count)[0] ?? null;
  }, [analysis]);
  const maxMonthlyCount = useMemo(() => {
    if (!analysis?.monthly.length) return 0;
    return analysis.monthly.reduce((max, month) => Math.max(max, month.count), 0);
  }, [analysis]);
  const sortedMonthsDesc = useMemo(() => {
    if (!analysis) return [];
    return [...analysis.monthly].sort((a, b) => b.count - a.count);
  }, [analysis]);
  const topMonths = sortedMonthsDesc.slice(0, 3);
  const topMonthConcentration = useMemo(() => {
    if (!analysis || !analysis.totalDemandUnits) return 0;
    const topTotal = topMonths.reduce((sum, m) => sum + m.count, 0);
    return Math.round((topTotal / analysis.totalDemandUnits) * 100);
  }, [analysis, topMonths]);
  const strongestDaypart = analysis?.daypartDemand[0];
  const coverageRisk = useMemo<DemandLevel>(() => {
    if (!analysis) return 'low';
    const demandSignal = analysis.operatingDays > 0 ? analysis.averageDemandOnOperatingDays : analysis.averageDailyDemand;
    const score = analysis.peakDemand * 0.45 + topMonthConcentration * 0.08 + demandSignal * 0.35;
    if (score >= 9) return 'high';
    if (score >= 5) return 'medium';
    return 'low';
  }, [analysis, topMonthConcentration]);
  const operatingDaysPreview = useMemo(() => {
    if (!from || !to || from > to) return 0;
    return countOperatingDaysInRange(from, to, operatingWeekdays, blackouts);
  }, [from, to, operatingWeekdays, blackouts]);
  const totalShiftsPerWeek = useMemo(() => tempPlan.reduce((sum, role) => sum + role.shiftsPerWeek, 0), [tempPlan]);
  const projectedWeeklyHours = totalShiftsPerWeek * shiftHours;
  const projectedWeeklyBudget = Math.round(projectedWeeklyHours * hourlyRate);
  const projectedPeriodBudget = projectedWeeklyBudget * leadWeeks;
  const conservativeBudget = Math.round(projectedPeriodBudget * 0.88);
  const aggressiveBudget = Math.round(projectedPeriodBudget * (1 + agencyBufferPercent / 100));
  const budgetCapBreached =
    annualBudgetCap !== '' && annualBudgetCap > 0 && projectedPeriodBudget > annualBudgetCap;
  const hiringTimeline = useMemo(() => {
    if (!analysis) return [];
    const sourceMonths = topMonths.length ? topMonths : analysis.monthly.slice(0, 1);
    return sourceMonths.map((month, index) => {
      const monthStart = monthStartIso(month.key);
      const recruitBy = shiftIsoDays(monthStart, -(leadWeeks * 7));
      const shortlistBy = shiftIsoDays(monthStart, -(Math.max(1, leadWeeks - 2) * 7));
      const onboardBy = shiftIsoDays(monthStart, -trainingDays);
      return {
        wave: `Wave ${index + 1}`,
        month: month.label,
        recruitBy,
        shortlistBy,
        onboardBy,
      };
    });
  }, [analysis, topMonths, leadWeeks, trainingDays]);

  function onPresetChange(next: PeriodPreset) {
    setPeriodPreset(next);
    if (next !== 'custom') {
      const bounds = buildPeriodBounds(next);
      setFrom(bounds.from);
      setTo(bounds.to);
    }
  }

  function rebuildTempPlan() {
    if (!analysis) return;
    setTempPlan(buildTempPlan(analysis, businessType, baseTeamSize));
  }

  function moveStep(delta: number) {
    setStepIndex((current) => Math.max(0, Math.min(YAA_REPORT_STEPS.length - 1, current + delta)));
  }

  function toggleWeekday(index: number) {
    setOperatingWeekdays((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }

  function addBlackout() {
    const today = toIsoDate(new Date());
    setBlackouts((prev) => [
      ...prev,
      { id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `b-${Date.now()}`, from: today, to: today },
    ]);
  }

  function updateBlackout(id: string, field: 'from' | 'to', value: string) {
    setBlackouts((prev) => prev.map((b) => (b.id === id ? { ...b, [field]: value } : b)));
  }

  function removeBlackout(id: string) {
    setBlackouts((prev) => prev.filter((b) => b.id !== id));
  }

  async function copyActionSummary() {
    if (!analysis) return;
    const summary = [
      `Your Year Ahead (${formatDateRange(from, to)})`,
      `Town: ${TOWNS.find((t) => t.slug === town)?.name ?? 'Regional'}`,
      `Events: ${analysis.totalEvents}`,
      `Operating days: ${analysis.operatingDays} (closed: ${analysis.closedDays})`,
      `Avg demand on operating days: ${analysis.averageDemandOnOperatingDays}`,
      `Peak day: ${analysis.bestDay ? `${analysis.bestDay.label} (${analysis.bestDay.count} events)` : 'None'}`,
      `Top months: ${topMonths.map((m) => `${m.label} (${m.count})`).join(', ') || 'None'}`,
      `Strongest daypart: ${strongestDaypart ? `${strongestDaypart.label} ${strongestDaypart.percentage}%` : 'None'}`,
      `Temp shifts/week: ${totalShiftsPerWeek}`,
      `Projected weekly temp budget: $${projectedWeeklyBudget.toLocaleString('en-AU')}`,
      `Projected period budget: $${projectedPeriodBudget.toLocaleString('en-AU')}`,
      `Coverage risk: ${coverageRisk}`,
    ].join('\n');

    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  function generatePdfReport() {
    if (!analysis) return;
    const origin = window.location.origin;
    const reportTitle = `Your Year Ahead — ${LJP_NENW_TITLE} — ${formatDateRange(from, to)}`;
    const categoryRows = analysis.categoryDemand
      .map((category) => `<tr><td>${escapeHtml(category.label)}</td><td>${category.count}</td><td>${category.share}%</td></tr>`)
      .join('');
    const monthRows = analysis.monthly
      .map((month) => `<tr><td>${escapeHtml(month.label)}</td><td>${month.count}</td><td>${month.level}</td></tr>`)
      .join('');
    const planRows = tempPlan
      .map(
        (job) =>
          `<tr><td>${escapeHtml(job.role)}</td><td>${job.shiftsPerWeek}</td><td>${escapeHtml(job.shift)}</td><td>${escapeHtml(job.window)}</td></tr>`,
      )
      .join('');
    const timelineRows = hiringTimeline
      .map(
        (row) =>
          `<tr><td>${escapeHtml(row.wave)}</td><td>${escapeHtml(row.month)}</td><td>${row.recruitBy}</td><td>${row.shortlistBy}</td><td>${row.onboardBy}</td></tr>`,
      )
      .join('');

    const chartTrend = buildPdfMonthlyTrendSvg(analysis.monthly, 680, 240);
    const chartMonthBars = buildPdfMonthlyBarsSvg(analysis.monthly, maxMonthlyCount);
    const chartWeekday = buildPdfWeekdaySvg(analysis.weekdayDemand);
    const chartDaypart = buildPdfDaypartSvg(analysis.daypartDemand);
    const chartCategory =
      analysis.categoryDemand.length > 0 ? buildPdfCategoryBarsSvg(analysis.categoryDemand) : '';

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
      <head>
        <title>${escapeHtml(reportTitle)}</title>
        <style>
          @media print {
            .chart-block { page-break-inside: avoid; break-inside: avoid; }
          }
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; margin: 22px; color: #0f172a; }
          .pdf-brand-row { display: flex; flex-wrap: wrap; align-items: center; gap: 16px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 2px solid #e2e8f0; }
          .pdf-brand-row img { display: block; }
          h1 { margin: 0 0 6px; font-size: 22px; line-height: 1.25; }
          .program-title { margin: 0 0 10px; font-size: 15px; font-weight: 700; color: #0c4a6e; }
          .wf-note { margin: 0 0 14px; font-size: 12px; color: #475569; }
          h2 { margin: 18px 0 8px; font-size: 17px; page-break-after: avoid; }
          p { margin: 4px 0; }
          .kpis { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 8px; margin-top: 8px; }
          .kpi { border: 1px solid #dbe4f2; border-radius: 10px; padding: 8px 10px; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th, td { border: 1px solid #dbe4f2; padding: 7px 8px; text-align: left; font-size: 13px; }
          th { background: #f3f8ff; }
          .meta { color: #475569; font-size: 13px; }
          .chart-block { margin: 14px 0; border: 1px solid #e2e8f0; border-radius: 12px; padding: 10px; background: #fafafa; }
          .chart-block svg { display: block; max-width: 100%; height: auto; }
        </style>
      </head>
      <body>
        <div class="pdf-brand-row">
          <img src="${origin}/branding/workforce-australia.svg" height="52" alt="Workforce Australia" />
          <img src="${origin}/branding/local-jobs-nenw.svg" height="48" alt="Local Jobs Program NENW" />
        </div>
        <h1>${escapeHtml(`Your Year Ahead — ${formatDateRange(from, to)}`)}</h1>
        <p class="program-title">${escapeHtml(LJP_NENW_TITLE)}</p>
        <p class="wf-note">This report is generated for workforce and local jobs planning. Charts summarise regional event-calendar demand signals.</p>
        <p class="meta">Town: ${escapeHtml(TOWNS.find((t) => t.slug === town)?.name ?? 'Regional')}</p>
        <p class="meta">Generated: ${new Date().toLocaleString('en-AU')}</p>

        <h2>Headline metrics</h2>
        <div class="kpis">
          <div class="kpi"><strong>Events in range:</strong> ${analysis.totalEvents}</div>
          <div class="kpi"><strong>Average demand/day (calendar):</strong> ${analysis.averageDailyDemand}</div>
          <div class="kpi"><strong>Operating days:</strong> ${analysis.operatingDays} (closed: ${analysis.closedDays})</div>
          <div class="kpi"><strong>Avg on operating days:</strong> ${analysis.averageDemandOnOperatingDays}</div>
          <div class="kpi"><strong>Peak day load:</strong> ${analysis.peakDemand}</div>
          <div class="kpi"><strong>Top weekday:</strong> ${escapeHtml(strongestWeekday?.label ?? '-')}</div>
        </div>

        <h2>Charts — seasonality</h2>
        <div class="chart-block">${chartTrend}</div>
        <div class="chart-block">${chartMonthBars}</div>

        <h2>Charts — day &amp; time</h2>
        <div class="chart-block">${chartWeekday}</div>
        <div class="chart-block">${chartDaypart}</div>

        ${chartCategory ? `<h2>Charts — category mix</h2><div class="chart-block">${chartCategory}</div>` : ''}

        <h2>Monthly demand (table)</h2>
        <table><thead><tr><th>Month</th><th>Demand units</th><th>Level</th></tr></thead><tbody>${monthRows}</tbody></table>

        <h2>Category mix (table)</h2>
        <table><thead><tr><th>Category</th><th>Demand units</th><th>Share</th></tr></thead><tbody>${categoryRows}</tbody></table>

        <h2>Temp jobs plan</h2>
        <table><thead><tr><th>Role</th><th>Shifts/week</th><th>Shift window</th><th>Priority months</th></tr></thead><tbody>${planRows}</tbody></table>

        <h2>Hiring timeline</h2>
        <table><thead><tr><th>Wave</th><th>Target month</th><th>Recruit by</th><th>Shortlist by</th><th>Onboard by</th></tr></thead><tbody>${timelineRows}</tbody></table>

        <h2>Budget scenarios</h2>
        <p><strong>Conservative:</strong> $${conservativeBudget.toLocaleString('en-AU')}</p>
        <p><strong>Base:</strong> $${projectedPeriodBudget.toLocaleString('en-AU')}</p>
        <p><strong>Aggressive (+${agencyBufferPercent}%):</strong> $${aggressiveBudget.toLocaleString('en-AU')}</p>
        ${
          annualBudgetCap !== '' && annualBudgetCap > 0
            ? `<p><strong>Soft budget cap:</strong> $${annualBudgetCap.toLocaleString('en-AU')} (${budgetCapBreached ? 'base scenario exceeds cap' : 'base within cap'})</p>`
            : ''
        }
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    window.setTimeout(() => printWindow.print(), 400);
  }

  const chartHeavySteps = new Set([1, 3, 4, 5, 6]);

  return (
    <div className={`scheduler-page scheduler-page--yaa ${phase === 'setup' ? 'scheduler-page--yaa-setup' : ''}`}>
      {phase === 'setup' ? (
        <div className="yaa-immersive-backdrop yaa-immersive-backdrop--setup" role="dialog" aria-modal="true" aria-labelledby="yaa-setup-title">
          <div className="yaa-immersive-panel yaa-immersive-panel--setup">
            <div className="yaa-immersive-toolbar yaa-immersive-toolbar--setup">
              <div className="yaa-immersive-toolbar-text">
                <p className="yaa-immersive-eyebrow yaa-immersive-eyebrow--setup">Your Year Ahead</p>
                <h1 id="yaa-setup-title">Plan setup</h1>
                <p className="yaa-immersive-toolbar-sub yaa-immersive-toolbar-sub--setup">
                  Period, region, availability, budgets — then generate your outlook.
                </p>
              </div>
              <div className="yaa-immersive-toolbar-actions">
                <Link to="/dashboard" className="yaa-link-toolbar yaa-link-toolbar--setup">
                  Dashboard
                </Link>
                {analysis && (
                  <button type="button" className="yaa-btn-toolbar-primary" onClick={() => setPhase('insights')}>
                    View outlook
                  </button>
                )}
              </div>
            </div>

            <div className="yaa-immersive-scroll yaa-immersive-scroll--setup">
              {error && <div className="scheduler-error yaa-error-in-modal">{error}</div>}

              <div className="yaa-setup-grid">
            <section className="yaa-setup-card">
              <h2>Date range</h2>
              <p className="yaa-setup-hint">Pick a preset or set exact start and end dates.</p>
              <div className="scheduler-form">
                <label className="yaa-label">
                  <span>Quick range</span>
                  <select value={periodPreset} onChange={(e) => onPresetChange(e.target.value as PeriodPreset)}>
                    <option value="next90">Next 90 days</option>
                    <option value="next180">Next 180 days</option>
                    <option value="yearAhead">Next 12 months</option>
                    <option value="financialYear">Current financial year</option>
                    <option value="custom">Custom</option>
                  </select>
                </label>
                <div className="scheduler-row">
                  <label className="yaa-label">
                    <span>Start</span>
                    <input
                      type="date"
                      value={from}
                      onChange={(e) => {
                        setPeriodPreset('custom');
                        setFrom(e.target.value);
                      }}
                    />
                  </label>
                  <label className="yaa-label">
                    <span>End</span>
                    <input
                      type="date"
                      value={to}
                      onChange={(e) => {
                        setPeriodPreset('custom');
                        setTo(e.target.value);
                      }}
                    />
                  </label>
                </div>
                <p className="yaa-setup-meta">
                  {from && to && from <= to
                    ? `${eachDay(from, to).length} calendar days · ${operatingDaysPreview} operating days with your availability rules`
                    : 'Enter a valid range.'}
                </p>
              </div>
            </section>

            <section className="yaa-setup-card">
              <h2>Region</h2>
              <p className="yaa-setup-hint">Filter published events by town or keep regional.</p>
              <div className="scheduler-form">
                <label className="yaa-label">
                  <span>Town / area</span>
                  <select value={town} onChange={(e) => setTown(e.target.value)}>
                    {TOWNS.map((t) => (
                      <option key={t.slug} value={t.slug}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </section>

            <section className="yaa-setup-card yaa-setup-card-wide">
              <h2>Availability</h2>
              <p className="yaa-setup-hint">
                Days you are closed are excluded from &quot;operating day&quot; averages. Add closures for holidays, renovations, or low-trade periods.
              </p>
              <div className="yaa-weekday-row">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label, index) => (
                  <button
                    key={label}
                    type="button"
                    className={`yaa-weekday-pill ${operatingWeekdays[index] ? 'on' : 'off'}`}
                    onClick={() => toggleWeekday(index)}
                    aria-pressed={operatingWeekdays[index]}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="yaa-setup-meta">Grey = closed every week on that weekday.</p>
              <div className="yaa-blackout-list">
                <div className="yaa-blackout-head">
                  <strong>Extra closed periods</strong>
                  <button type="button" className="yaa-btn-ghost" onClick={addBlackout}>
                    Add dates
                  </button>
                </div>
                {blackouts.length === 0 ? (
                  <p className="scheduler-empty">No extra closures. Optional: add school holidays, stocktake weeks, etc.</p>
                ) : (
                  blackouts.map((b) => (
                    <div key={b.id} className="yaa-blackout-row">
                      <input type="date" value={b.from} onChange={(e) => updateBlackout(b.id, 'from', e.target.value)} />
                      <span className="yaa-blackout-to">to</span>
                      <input type="date" value={b.to} onChange={(e) => updateBlackout(b.id, 'to', e.target.value)} />
                      <button type="button" className="yaa-btn-ghost" onClick={() => removeBlackout(b.id)}>
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="yaa-setup-card">
              <h2>Business &amp; team</h2>
              <p className="yaa-setup-hint">Used for temp role templates and shift estimates.</p>
              <div className="scheduler-form">
                <label className="yaa-label">
                  <span>Business type</span>
                  <select value={businessType} onChange={(e) => setBusinessType(e.target.value as BusinessType)}>
                    <option value="hospitality">Hospitality</option>
                    <option value="retail">Retail</option>
                    <option value="accommodation">Accommodation</option>
                    <option value="tourism">Tourism</option>
                    <option value="services">Services</option>
                  </select>
                </label>
                <label className="yaa-label">
                  <span>Core team size (FTE baseline)</span>
                  <input
                    type="number"
                    min={1}
                    max={300}
                    value={baseTeamSize}
                    onChange={(e) => setBaseTeamSize(Number(e.target.value) || 1)}
                  />
                </label>
              </div>
            </section>

            <section className="yaa-setup-card yaa-setup-card-wide">
              <h2>Budget defaults</h2>
              <p className="yaa-setup-hint">Rough temp labour costs for scenarios and PDF export. Adjust anytime here or in the outlook.</p>
              <div className="yaa-budget-grid">
                <label className="yaa-label">
                  <span>Hourly rate (AUD)</span>
                  <input
                    type="number"
                    min={20}
                    max={200}
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(Number(e.target.value) || 20)}
                  />
                </label>
                <label className="yaa-label">
                  <span>Hours per temp shift</span>
                  <input
                    type="number"
                    min={3}
                    max={12}
                    value={shiftHours}
                    onChange={(e) => setShiftHours(Number(e.target.value) || 3)}
                  />
                </label>
                <label className="yaa-label">
                  <span>Weeks of cover to budget</span>
                  <input
                    type="number"
                    min={1}
                    max={52}
                    value={leadWeeks}
                    onChange={(e) => setLeadWeeks(Number(e.target.value) || 1)}
                  />
                </label>
                <label className="yaa-label">
                  <span>Training days before peak</span>
                  <input
                    type="number"
                    min={0}
                    max={14}
                    value={trainingDays}
                    onChange={(e) => setTrainingDays(Number(e.target.value) || 0)}
                  />
                </label>
                <label className="yaa-label">
                  <span>Agency / contingency buffer (%)</span>
                  <input
                    type="number"
                    min={0}
                    max={50}
                    value={agencyBufferPercent}
                    onChange={(e) => setAgencyBufferPercent(Number(e.target.value) || 0)}
                  />
                </label>
                <label className="yaa-label">
                  <span>Soft budget cap for period (optional AUD)</span>
                  <input
                    type="number"
                    min={0}
                    placeholder="No cap"
                    value={annualBudgetCap === '' ? '' : annualBudgetCap}
                    onChange={(e) => {
                      const v = e.target.value;
                      setAnnualBudgetCap(v === '' ? '' : Number(v) || 0);
                    }}
                  />
                </label>
              </div>
              {annualBudgetCap !== '' && annualBudgetCap > 0 && (
                <p className="yaa-setup-meta">
                  Estimated base period cost: ${projectedPeriodBudget.toLocaleString('en-AU')}
                  {budgetCapBreached ? ' — exceeds your cap; tighten shifts or extend lead time.' : ' — within cap.'}
                </p>
              )}
            </section>

            <section className="yaa-setup-card yaa-setup-card-cta">
              <h2>Generate outlook</h2>
              <p className="yaa-setup-hint">Pulls published events for your range and region, then opens your Year Ahead screens.</p>
              <button type="button" className="yaa-btn-primary" onClick={runYearAhead} disabled={loading}>
                {loading ? 'Generating…' : 'Generate my Year Ahead'}
              </button>
            </section>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="yaa-immersive-backdrop" role="dialog" aria-modal="true" aria-labelledby="yaa-outlook-title">
          <div className="yaa-immersive-panel yaa-immersive-panel--outlook">
            <div className="yaa-immersive-toolbar yaa-immersive-toolbar--outlook">
              <div className="yaa-immersive-toolbar-text">
                <p className="yaa-immersive-eyebrow">Your Year Ahead</p>
                <h1 id="yaa-outlook-title">Outlook</h1>
                <p className="yaa-immersive-toolbar-sub">
                  {formatDateRange(from, to)} · {TOWNS.find((t) => t.slug === town)?.name ?? 'Regional'}
                </p>
              </div>
              <div className="yaa-immersive-toolbar-actions">
                <button type="button" className="yaa-btn-toolbar-secondary" onClick={() => setPhase('setup')}>
                  Plan setup
                </button>
                <Link to="/dashboard" className="yaa-link-toolbar">
                  Dashboard
                </Link>
              </div>
            </div>

            {error && <div className="scheduler-error yaa-error-in-modal">{error}</div>}

            {analysis ? (
              <>
                <div className="yaa-outlook-progress">
                  <p className="yaa-outlook-progress-label">
                    Screen {stepIndex + 1} / {YAA_REPORT_STEPS.length} — {YAA_REPORT_STEPS[stepIndex]}
                  </p>
                  <div className="wrapped-progress-track yaa-outlook-progress-bar">
                    <div
                      className="wrapped-progress-fill"
                      style={{ width: `${((stepIndex + 1) / YAA_REPORT_STEPS.length) * 100}%` }}
                    />
                  </div>
                  <div className="wrapped-step-pills yaa-outlook-pills">
                    {YAA_REPORT_STEPS.map((step, index) => (
                      <button
                        key={step}
                        type="button"
                        className={`step-pill ${index === stepIndex ? 'active' : ''}`}
                        onClick={() => setStepIndex(index)}
                        title={step}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                </div>

                <div
                  className={`yaa-outlook-slide ${chartHeavySteps.has(stepIndex) ? 'yaa-outlook-slide--charts' : ''}`}
                >
          {stepIndex === 0 && (
            <article className="scheduler-panel yaa-insight-panel">
              <h3>Headline snapshot</h3>
              <div className="scheduler-kpis yaa-headline-kpis">
                <div>
                  <small>Events in range</small>
                  <h3>{analysis.totalEvents}</h3>
                </div>
                <div>
                  <small>Avg demand / calendar day</small>
                  <h3>{analysis.averageDailyDemand}</h3>
                </div>
                <div>
                  <small>Avg on operating days</small>
                  <h3>{analysis.averageDemandOnOperatingDays}</h3>
                </div>
                <div>
                  <small>Operating / closed days</small>
                  <h3>
                    {analysis.operatingDays} / {analysis.closedDays}
                  </h3>
                </div>
                <div>
                  <small>Peak day load</small>
                  <h3>{analysis.peakDemand}</h3>
                </div>
                <div>
                  <small>Top weekday</small>
                  <h3>{strongestWeekday?.label ?? '-'}</h3>
                </div>
              </div>
              <div className="wrapped-banner">
                <p>{formatDateRange(from, to)}</p>
                <h3>{analysis.bestDay ? analysis.bestDay.label : 'No peak day yet'}</h3>
                <small>{analysis.bestDay ? `${analysis.bestDay.count} overlapping events` : 'No event overlap detected'}</small>
              </div>
            </article>
          )}

          {stepIndex === 1 && (
            <article className="scheduler-panel yaa-insight-panel">
              <h3>Seasonality graph</h3>
              <div className="line-chart-card">
                <svg viewBox="0 0 680 240" role="img" aria-label="Monthly demand trend graph">
                  <polyline points={monthPathPoints(analysis.monthly, 680, 240, 24)} className="month-line" />
                  {analysis.monthly.map((month, index) => {
                    const x = 24 + (analysis.monthly.length === 1 ? 316 : (index / (analysis.monthly.length - 1)) * 632);
                    const y = 24 + 192 - (month.count / Math.max(maxMonthlyCount, 1)) * 192;
                    return <circle key={month.key} cx={x} cy={y} r="4.5" className={`month-dot demand-${month.level}`} />;
                  })}
                </svg>
                <div className="line-chart-labels">
                  {analysis.monthly.map((month) => (
                    <span key={month.key}>{month.label}</span>
                  ))}
                </div>
              </div>
              <div className="scheduler-month-bars">
                {analysis.monthly.map((month) => (
                  <div key={month.key} className="month-bar-row">
                    <span>{month.label}</span>
                    <div className="month-bar-track">
                      <div
                        className={`month-bar-fill demand-${month.level}`}
                        style={{ width: `${Math.max(4, maxMonthlyCount ? Math.round((month.count / maxMonthlyCount) * 100) : 0)}%` }}
                      />
                    </div>
                    <strong>{month.count}</strong>
                  </div>
                ))}
              </div>
            </article>
          )}

          {stepIndex === 2 && (
            <article className="scheduler-panel yaa-insight-panel">
              <h3>Category demand mix</h3>
              <div className="category-grid">
                {analysis.categoryDemand.map((category) => (
                  <div key={category.label} className="category-card">
                    <p>{category.label}</p>
                    <h4>{category.count}</h4>
                    <small>{category.share}% of weighted demand</small>
                  </div>
                ))}
              </div>
              <p className="scheduler-empty">
                {analysis.quietestMonth
                  ? `Quietest month: ${analysis.quietestMonth.label} (${analysis.quietestMonth.count} demand units).`
                  : 'No quiet month detected yet.'}
              </p>
            </article>
          )}

          {stepIndex === 3 && (
            <article className="scheduler-panel yaa-insight-panel">
              <h3>Day and time behaviour</h3>
              <div className="scheduler-grid">
                <div className="weekday-chart">
                  {analysis.weekdayDemand.map((day) => {
                    const max = Math.max(...analysis.weekdayDemand.map((d) => d.count), 1);
                    return (
                      <div key={day.label} className="weekday-bar-row">
                        <span>{day.label}</span>
                        <div className="weekday-track">
                          <div className="weekday-fill" style={{ width: `${Math.round((day.count / max) * 100)}%` }} />
                        </div>
                        <strong>{day.count}</strong>
                      </div>
                    );
                  })}
                </div>
                <div className="daypart-ring-panel">
                  <div className="daypart-ring" style={{ background: conicDaypartGradient(analysis.daypartDemand) }}>
                    <div className="daypart-ring-hole">
                      <strong>{strongestDaypart?.percentage ?? 0}%</strong>
                      <small>{strongestDaypart?.label ?? 'No daypart data'}</small>
                    </div>
                  </div>
                  <div className="scheduler-dayparts">
                    {analysis.daypartDemand.map((part) => (
                      <div key={part.key} className="daypart-chip">
                        <span>{part.label}</span>
                        <strong>{part.percentage}%</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          )}

          {stepIndex === 4 && (
            <article className="scheduler-panel yaa-insight-panel">
              <h3>Peak pressure windows</h3>
              <div className="risk-banner">
                <p>Coverage risk</p>
                <h3 className={`demand-${coverageRisk}`}>{coverageRisk.toUpperCase()}</h3>
                <small>{topMonthConcentration}% of demand is concentrated in your top 3 months.</small>
              </div>
              {analysis.busiestStreak && (
                <p className="scheduler-empty">
                  Longest pressure streak: {analysis.busiestStreak.start} to {analysis.busiestStreak.end} ({analysis.busiestStreak.days} days, avg{' '}
                  {analysis.busiestStreak.avgDemand} demand).
                </p>
              )}
              <div className="scheduler-timeline">
                {analysis.topDemandDays.map((day) => (
                  <div key={day.iso} className="scheduler-day">
                    <span>{day.label}</span>
                    <strong className={`demand-${demandLevel(day.count, analysis.peakDemand * 0.45, analysis.peakDemand * 0.8)}`}>
                      {day.count} events
                    </strong>
                    <small>{day.iso}</small>
                  </div>
                ))}
              </div>
            </article>
          )}

          {stepIndex === 5 && (
            <article className="scheduler-panel yaa-insight-panel">
              <h3>Temp staffing plan</h3>
              <p className="scheduler-empty">
                Business type, team size, and rates are set in <strong>Plan setup</strong>. Refresh after you change them.
              </p>
              <div className="handoff-row">
                <button type="button" onClick={rebuildTempPlan}>Refresh staffing plan</button>
              </div>
              <div className="budget-kpis">
                <div>
                  <small>Shifts / week</small>
                  <h3>{totalShiftsPerWeek}</h3>
                </div>
                <div>
                  <small>Weekly hours</small>
                  <h3>{projectedWeeklyHours}</h3>
                </div>
                <div>
                  <small>Weekly budget</small>
                  <h3>${projectedWeeklyBudget.toLocaleString('en-AU')}</h3>
                </div>
                <div>
                  <small>{leadWeeks}-week budget</small>
                  <h3>${projectedPeriodBudget.toLocaleString('en-AU')}</h3>
                </div>
              </div>
              <div className="temp-plan-list">
                {tempPlan.map((job) => (
                  <div key={job.role} className="temp-plan-card">
                    <p>{job.role}</p>
                    <h3>{job.shiftsPerWeek} shifts / week</h3>
                    <small>{job.shift}</small>
                    <p>{job.window}</p>
                    <small>{job.reason}</small>
                  </div>
                ))}
              </div>
            </article>
          )}

          {stepIndex === 6 && (
            <article className="scheduler-panel yaa-insight-panel">
              <h3>Hiring timeline by wave</h3>
              <div className="timeline-table-wrap">
                <table className="timeline-table">
                  <thead>
                    <tr>
                      <th>Wave</th>
                      <th>Target month</th>
                      <th>Recruit by</th>
                      <th>Shortlist by</th>
                      <th>Onboard by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hiringTimeline.map((row) => (
                      <tr key={row.wave}>
                        <td>{row.wave}</td>
                        <td>{row.month}</td>
                        <td>{row.recruitBy}</td>
                        <td>{row.shortlistBy}</td>
                        <td>{row.onboardBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          )}

          {stepIndex === 7 && (
            <article className="scheduler-panel yaa-insight-panel">
              <h3>Budget scenarios</h3>
              <div className="scenario-grid">
                <div className="scenario-card">
                  <p>Conservative</p>
                  <h4>${conservativeBudget.toLocaleString('en-AU')}</h4>
                  <small>Lean staffing with strict shift control.</small>
                </div>
                <div className="scenario-card">
                  <p>Base</p>
                  <h4>${projectedPeriodBudget.toLocaleString('en-AU')}</h4>
                  <small>Recommended operating plan for current demand profile.</small>
                </div>
                <div className="scenario-card">
                  <p>Aggressive</p>
                  <h4>${aggressiveBudget.toLocaleString('en-AU')}</h4>
                  <small>Includes agency + contingency buffer of {agencyBufferPercent}%.</small>
                </div>
              </div>
              {annualBudgetCap !== '' && annualBudgetCap > 0 && (
                <p className={`scheduler-empty ${budgetCapBreached ? 'yaa-cap-warn' : ''}`}>
                  Your soft cap: ${annualBudgetCap.toLocaleString('en-AU')}.
                  {budgetCapBreached ? ' Base scenario exceeds cap — revisit setup or scenarios.' : ' Base scenario is within cap.'}
                </p>
              )}
            </article>
          )}

          {stepIndex === 8 && (
            <article className="scheduler-panel yaa-insight-panel">
              <h3>Generate your PDF report</h3>
              <p className="scheduler-empty">
                Includes embedded charts (seasonality, weekday, daypart, categories), all tables, {LJP_NENW_TITLE}, and
                Workforce Australia + Local Jobs Program NENW logos.
              </p>
              <div className="handoff-row">
                <button type="button" onClick={generatePdfReport}>Generate PDF report</button>
                <small>Opens print dialog so you can save as PDF.</small>
              </div>
            </article>
          )}

          {stepIndex === 9 && (
            <article className="scheduler-panel yaa-insight-panel">
              <h3>Checklist &amp; handoff</h3>
              <div className="checklist-card">
                <label>
                  <input
                    type="checkbox"
                    checked={checklist.lockRoster}
                    onChange={(e) => setChecklist((prev) => ({ ...prev, lockRoster: e.target.checked }))}
                  />
                  Lock roster for the top demand months ({topMonths.map((m) => m.label).join(', ') || 'n/a'})
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={checklist.postJobs}
                    onChange={(e) => setChecklist((prev) => ({ ...prev, postJobs: e.target.checked }))}
                  />
                  Post temp roles {leadWeeks} weeks before peak windows
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={checklist.confirmTraining}
                    onChange={(e) => setChecklist((prev) => ({ ...prev, confirmTraining: e.target.checked }))}
                  />
                  Confirm onboarding and training slots
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={checklist.checkSuppliers}
                    onChange={(e) => setChecklist((prev) => ({ ...prev, checkSuppliers: e.target.checked }))}
                  />
                  Validate stock/supplier support for top weekdays
                </label>
              </div>
              <div className="handoff-row">
                <button type="button" onClick={copyActionSummary}>Copy planning summary</button>
                <small>{copied ? 'Copied to clipboard' : 'Copy summary for your ops team or agency partner'}</small>
              </div>
            </article>
          )}

                </div>

                <footer className="yaa-outlook-footer">
                  <button
                    type="button"
                    className="yaa-outlook-nav-btn"
                    onClick={() => moveStep(-1)}
                    disabled={stepIndex === 0}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="yaa-outlook-nav-btn yaa-outlook-nav-btn--primary"
                    onClick={() => moveStep(1)}
                    disabled={stepIndex === YAA_REPORT_STEPS.length - 1}
                  >
                    Next
                  </button>
                </footer>
              </>
            ) : (
              <div className="yaa-outlook-empty">
                <h3>No outlook yet</h3>
                <p className="scheduler-empty">Complete plan setup and generate your Year Ahead.</p>
                <button type="button" className="yaa-btn-toolbar-primary" onClick={() => setPhase('setup')}>
                  Open plan setup
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
